import React, { Component } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Factory, Building2, Zap, Package, Car, Shield, ArrowRight, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
interface ServicesPageProps {
  onOpenSampleModal?: () => void;
}
export function ServicesPage({
  onOpenSampleModal
}: ServicesPageProps) {
  return <div className="min-h-screen bg-white font-sans text-[#0A1628] selection:bg-[#0052CC] selection:text-white overflow-x-hidden" data-id="element-1287">
      <Header onOpenSampleModal={onOpenSampleModal} data-id="element-1288" />

      <main className="pt-[135px] md:pt-[145px]" data-id="element-1289">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-white" data-id="element-1290">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center" data-id="element-1291">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1292">
              <span className="text-xs font-bold text-[#0052CC] uppercase tracking-widest mb-4 block" data-id="element-1293">
                SECTORS
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0A1628] tracking-tight leading-[1.08] mb-6" data-id="element-1294">
                Industries we serve
              </h1>
              <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1295">
                Trusted labelling solutions for critical sectors across the UK
                and Europe
              </p>
            </motion.div>
          </div>
        </section>

        {/* Sectors Grid Section */}
        <section className="py-16 md:py-24 bg-slate-50" data-id="element-1296">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1297">
            {/* Section Header */}
            <div className="text-center mb-16" data-id="element-1298">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 block" data-id="element-1299">
                Sectors
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-1300">
                Solutions built for your industry
              </h2>
              <p className="text-lg text-slate-500 max-w-xl mx-auto" data-id="element-1301">
                Each sector has distinct labelling demands. We understand them
                all.
              </p>
            </div>

            {/* Asymmetric Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1302">
              {/* Left Column */}
              <div className="flex flex-col gap-6" data-id="element-1303">
                {/* Manufacturing - Tall Card */}
                <motion.a href="/services/manufacturing" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} className="group bg-white border border-slate-100 rounded-2xl p-8 hover:shadow-lg hover:border-slate-200 transition-all duration-300 flex flex-col" data-id="element-1304">
                  <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0052CC]/10 transition-colors" data-id="element-1305">
                    <Factory className="h-6 w-6 text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1306" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1307">
                    Manufacturing
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6 flex-1" data-id="element-1308">
                    Asset tracking, machinery identification, and durability
                    under production stress
                  </p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1309">
                    View
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1310" />
                  </span>
                </motion.a>

                {/* Facilities Management */}
                <motion.a href="/services/facilities" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.1
              }} className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg hover:border-slate-200 transition-all duration-300" data-id="element-1311">
                  <div className="p-8" data-id="element-1312">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 block" data-id="element-1313">
                      Manufacturing
                    </span>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1314">
                      Facilities management
                    </h3>
                    <p className="text-slate-500 leading-relaxed mb-4" data-id="element-1315">
                      Safety compliance, maintenance scheduling, and equipment
                      identification across sites
                    </p>
                    <span className="inline-flex items-center text-sm font-semibold text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1316">
                      View
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1317" />
                    </span>
                  </div>
                  <div className="aspect-[16/9] bg-slate-100" data-id="element-1318">
                    <img src="https://cdn.magicpatterns.com/uploads/uH1CAGacPnUx7cXgA8uYKz/Nilpeter-FB-line.jpg" alt="Facilities management" className="w-full h-full object-cover" data-id="element-1319" />
                  </div>
                </motion.a>
              </div>

              {/* Center Column */}
              <div className="flex flex-col gap-6" data-id="element-1320">
                {/* Electrical */}
                <motion.a href="/services/electrical" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.15
              }} className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg hover:border-slate-200 transition-all duration-300" data-id="element-1321">
                  <div className="p-8" data-id="element-1322">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 block" data-id="element-1323">
                      Facilities
                    </span>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1324">
                      Electrical
                    </h3>
                    <p className="text-slate-500 leading-relaxed mb-4" data-id="element-1325">
                      Voltage warnings, circuit identification, and regulatory
                      compliance labelling
                    </p>
                    <span className="inline-flex items-center text-sm font-semibold text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1326">
                      View
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1327" />
                    </span>
                  </div>
                  <div className="aspect-[16/9] bg-slate-100" data-id="element-1328">
                    <img src="https://cdn.magicpatterns.com/uploads/1YEz3DVnhhkzQY2ULHBb9D/nameplates.jpg" alt="Electrical labelling" className="w-full h-full object-cover" data-id="element-1329" />
                  </div>
                </motion.a>

                {/* Warehousing & Logistics */}
                <motion.a href="/services/warehousing" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2
              }} className="group bg-white border border-slate-100 rounded-2xl overflow-hidden hover:shadow-lg hover:border-slate-200 transition-all duration-300" data-id="element-1330">
                  <div className="p-8" data-id="element-1331">
                    <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0052CC]/10 transition-colors" data-id="element-1332">
                      <Package className="h-6 w-6 text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1333" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1334">
                      Warehousing and logistics
                    </h3>
                    <p className="text-slate-500 leading-relaxed mb-4" data-id="element-1335">
                      Inventory management, location marking, and rapid product
                      identification systems
                    </p>
                    <span className="inline-flex items-center text-sm font-semibold text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1336">
                      View
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1337" />
                    </span>
                  </div>
                  <div className="aspect-[16/9] bg-slate-100" data-id="element-1338">
                    <img src="https://cdn.magicpatterns.com/uploads/uH1CAGacPnUx7cXgA8uYKz/Nilpeter-FB-line.jpg" alt="Warehousing and logistics" className="w-full h-full object-cover" data-id="element-1339" />
                  </div>
                </motion.a>
              </div>

              {/* Right Column */}
              <div className="flex flex-col gap-6" data-id="element-1340">
                {/* Automotive - Tall Card */}
                <motion.a href="/services/automotive" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.25
              }} className="group bg-white border border-slate-100 rounded-2xl p-8 hover:shadow-lg hover:border-slate-200 transition-all duration-300 flex flex-col" data-id="element-1341">
                  <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0052CC]/10 transition-colors" data-id="element-1342">
                    <Car className="h-6 w-6 text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1343" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1344">
                    Automotive
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6 flex-1" data-id="element-1345">
                    Component tracking, assembly line identification, and
                    quality assurance labelling
                  </p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1346">
                    View
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1347" />
                  </span>
                </motion.a>

                {/* Defence */}
                <motion.a href="/services/defence" initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.3
              }} className="group bg-white border border-slate-100 rounded-2xl p-8 hover:shadow-lg hover:border-slate-200 transition-all duration-300" data-id="element-1348">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 block" data-id="element-1349">
                    Automotive
                  </span>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3" data-id="element-1350">
                    Defence
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-4" data-id="element-1351">
                    High-security identification, traceability, and stringent
                    compliance requirements
                  </p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#0A1628] group-hover:text-[#0052CC] transition-colors" data-id="element-1352">
                    View
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1353" />
                  </span>
                </motion.a>
              </div>
            </div>
          </div>
        </section>

        {/* Bespoke / Tailored Section */}
        <section className="py-16 md:py-24 bg-white" data-id="element-1354">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1355">
            {/* Section Header */}
            <div className="text-center mb-16" data-id="element-1356">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 block" data-id="element-1357">
                Bespoke
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0A1628] tracking-tight mb-4" data-id="element-1358">
                Tailored for your needs
              </h2>
              <p className="text-lg text-slate-500 max-w-xl mx-auto" data-id="element-1359">
                Standard products cover most requirements. Sometimes you need
                something different.
              </p>
            </div>

            {/* Split Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white border border-slate-100 rounded-[32px] overflow-hidden" data-id="element-1360">
              <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-1361">
                {/* Left Content */}
                <div className="p-10 lg:p-16 flex flex-col justify-center" data-id="element-1362">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 block" data-id="element-1363">
                    Custom
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4 leading-tight" data-id="element-1364">
                    Sector-specific compliance and operational demands
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-8" data-id="element-1365">
                    We work with procurement and engineering teams to design
                    labels that fit your exact specifications. Whether it's
                    material, size, finish, or regulatory compliance, we build
                    solutions that work for your operation, not against it.
                  </p>
                  <div className="flex flex-wrap items-center gap-4" data-id="element-1366">
                    <a href="/contact" className="px-6 py-3 border border-[#0A1628] text-[#0A1628] font-semibold rounded-full hover:bg-[#0A1628] hover:text-white transition-colors" data-id="element-1367">
                      Enquire
                    </a>
                    <a href="/bespoke" className="inline-flex items-center text-sm font-semibold text-[#0A1628] hover:text-[#0052CC] transition-colors" data-id="element-1368">
                      Learn
                      <ArrowRight className="ml-1 h-4 w-4" data-id="element-1369" />
                    </a>
                  </div>
                </div>

                {/* Right Image */}
                <div className="aspect-[4/3] lg:aspect-auto bg-slate-100" data-id="element-1370">
                  <img src="https://cdn.magicpatterns.com/uploads/uH1CAGacPnUx7cXgA8uYKz/Nilpeter-FB-line.jpg" alt="Custom labelling solutions" className="w-full h-full object-cover" data-id="element-1371" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#0A1628]" data-id="element-1372">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12" data-id="element-1373">
            <div className="relative overflow-hidden" data-id="element-1374">
              {/* Subtle accent */}
              <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0052CC]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-1375"></div>

              <div className="relative z-10 max-w-2xl mx-auto text-center" data-id="element-1376">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-1377">
                  Need a bespoke solution?
                </h2>
                <p className="text-lg text-slate-300 mb-10 leading-relaxed" data-id="element-1378">
                  Our specialists have decades of experience solving industrial
                  identification challenges. We're here to help you find the
                  right solution.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-1379">
                  <a href="tel:01384243155" className="w-full sm:w-auto px-7 py-3.5 bg-[#0052CC] text-white font-bold rounded-full hover:bg-[#0040A0] transition-colors flex items-center justify-center group" data-id="element-1380">
                    <Phone className="mr-2 h-4 w-4" data-id="element-1381" />
                    Speak to our specialists
                  </a>
                  <button onClick={onOpenSampleModal} className="w-full sm:w-auto px-7 py-3.5 bg-white/10 border border-white/20 text-white font-bold rounded-full hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center" data-id="element-1382">
                    Request a sample pack
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-1383" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1384" />
    </div>;
}